import React, { useEffect, useState } from 'react';
import { useGlobal } from '../../context/GlobalContext';
import { formatPriceWithPrefix } from '../../lib/currency';
import { getStrapiMediaAttributes } from '../../lib/strapi';
import { ENGLISH_LOCALE, isEnglishLocale, isSpanishLocale } from '../../lib/locale';
import {
  isHomeSlug,
  resolveUrlForLocale,
} from '../../lib/localizedSlugs';
import { getContactTypeLabel, getUiText } from '../../lib/uiText';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FaWhatsapp, FaPhone, FaEnvelope, FaRegFileLines, FaChevronDown } from 'react-icons/fa6';
import styles from './Header.module.css';

const getViewportFlags = () => {
  if (typeof window === 'undefined') {
    return { isMobile: false, isLandscape: false };
  }

  return {
    isMobile: window.innerWidth <= 1024,
    isLandscape: window.matchMedia('(orientation: landscape)').matches,
  };
};

const Header = () => {
  const {
    header,
    companyInfo,
    loading,
    headerHidden,
    setHeaderHidden,
    priceMap,
    locale,
    changeLocale,
    availableLocales,
    localizedPagePaths,
    spanishLocale,
    englishLocale,
    homeLogoPath,
  } = useGlobal();
  const [isAtTop, setIsAtTop] = useState(true);
  const [navSubmenuOpen, setNavSubmenuOpen] = useState(false);
  const [contactMenuOpen, setContactMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [viewportFlags, setViewportFlags] = useState(getViewportFlags);
  const lastScrollY = React.useRef(window.scrollY);
  const location = useLocation();
  const navigate = useNavigate();

  const isHome = isHomeSlug(location.pathname);
  const isSticky = header?.isSticky;
  const isMobile = viewportFlags.isMobile;
  const supportsHover = typeof window !== 'undefined'
    && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const isAnySubmenuOpen = navSubmenuOpen || contactMenuOpen;

  const closeSubmenus = () => {
    setNavSubmenuOpen(false);
    setContactMenuOpen(false);
  };

  const closeMobileMenu = () => {
    closeSubmenus();
    setMobileMenuOpen(false);
  };

  // Close mobile menu on route change
  useEffect(() => {
    const frameId = window.requestAnimationFrame(() => {
      setNavSubmenuOpen(false);
      setContactMenuOpen(false);
      setMobileMenuOpen(false);
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  useEffect(() => {
    const handleResize = () => setViewportFlags(getViewportFlags());

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!isSticky) return;

    const handleScroll = () => {
      const currentY = window.scrollY;
      setIsAtTop(currentY <= 0);

      if (headerHidden && currentY !== lastScrollY.current) {
        setHeaderHidden(false);
        window.focus();
      }
      lastScrollY.current = currentY;
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isSticky, headerHidden, setHeaderHidden, mobileMenuOpen]);

  if (loading) {
    return <header className={`${styles.header} ${styles['header--loading']}`}></header>;
  }

  if (!header) return null;

  const logoMedia = header.useCompanyLogo && companyInfo?.logoMain
    ? getStrapiMediaAttributes(companyInfo.logoMain)
    : getStrapiMediaAttributes(header.logo);

  const logoTo = homeLogoPath;

  const handleNavClick = (e, item) => {
    closeSubmenus();

    if (item.scrollToSection) {
      e.preventDefault();
      const element = document.getElementById(item.scrollToSection);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const handleNavItemMouseEnter = (hasChildren) => {
    if (!supportsHover || !hasChildren) return;
    setNavSubmenuOpen(true);
  };

  const handleNavItemMouseLeave = (hasChildren) => {
    if (!supportsHover || !hasChildren) return;
    setNavSubmenuOpen(false);
  };

  const handleContactMouseEnter = () => {
    if (!supportsHover) return;
    setContactMenuOpen(true);
  };

  const handleContactMouseLeave = () => {
    if (!supportsHover) return;
    setContactMenuOpen(false);
  };

  const handleContactTriggerClick = (event) => {
    const isKeyboardActivation = event.detail === 0;

    if (supportsHover && !mobileMenuOpen && !isKeyboardActivation) {
      return;
    }

    setContactMenuOpen((prev) => !prev);
  };

  const showAtTop = isSticky && isAtTop && !mobileMenuOpen && (isHome || isMobile);
  const showScrolled = isSticky && (isMobile ? !isAtTop : (!isHome || !isAtTop));

  const mostrarPrecio = header?.mostrarPrecio !== false;
  const getSlugPrice = (url) => {
    if (!mostrarPrecio || !url || !priceMap) return null;
    const slug = url.replace(/^\//, '');
    const price = priceMap[slug];
    if (!price) return null;
    return formatPriceWithPrefix(price, companyInfo, locale);
  };

  const headerClassName = [
    styles.header,
    isSticky ? styles['header--sticky'] : null,
    showAtTop ? styles['header--at-top'] : null,
    showScrolled ? styles['header--scrolled'] : null,
    isSticky && headerHidden && !mobileMenuOpen ? styles['header--hidden'] : null,
    isAnySubmenuOpen ? styles['header--submenu-open'] : null,
    mobileMenuOpen ? styles['header--mobile-open'] : null,
    header?.textoOscuro ? styles['header--dark-text'] : null,
  ]
    .filter(Boolean)
    .join(' ');
  const headerBackgroundColor = header?.colorPersonalizado?.trim();
  const useCustomHeaderBackground = headerBackgroundColor
    && (typeof CSS === 'undefined' || typeof CSS.supports !== 'function' || CSS.supports('color', headerBackgroundColor));
  const headerStyle = useCustomHeaderBackground
    ? { '--color-header-footer': headerBackgroundColor }
    : undefined;
  const headerLogoHeight = header?.heightLogo?.trim();
  const headerLogoHeightMobile = header?.heightLogoMobile?.trim();
  const headerStyleWithLogoHeight = {
    ...(headerStyle || {}),
    ...(headerLogoHeight ? { '--header-logo-height': headerLogoHeight } : {}),
    ...(headerLogoHeightMobile ? { '--header-logo-height-mobile': headerLogoHeightMobile } : {}),
  };
  const resolvedHeaderStyle = Object.keys(headerStyleWithLogoHeight).length > 0
    ? headerStyleWithLogoHeight
    : undefined;

  const isEn = isEnglishLocale(locale);

  const toggleLocale = () => {
    const nextLocale = isEn ? spanishLocale : englishLocale;
    const nextLocaleKey = isEnglishLocale(nextLocale) ? 'en' : 'es';
    const nextPath = isHomeSlug(location.pathname) ? null : localizedPagePaths?.[nextLocaleKey];

    changeLocale(nextLocale);
    if (nextPath && nextPath !== location.pathname) {
      navigate(`${nextPath}${location.search || ''}${location.hash || ''}`);
    }
  };
  const isEnDisplay = isEn;

  // Build WhatsApp href: prefer item.value, fallback to companyInfo socialMedia/contactInfo
  const getFallbackWhatsappNumber = () => {
    const fromSocial = companyInfo?.socialMedia?.find((s) => s?.platform === 'whatsapp');
    if (fromSocial?.url) {
      const match = fromSocial.url.match(/(\d[\d\s\-+()]+)/);
      if (match) return match[1];
    }
    const fromContact = companyInfo?.contactInfo?.find((c) => c?.type === 'whatsapp');
    if (fromContact?.value) return fromContact.value;
    return null;
  };

  const getFallbackPhone = () => {
    const phones = companyInfo?.contactInfo?.filter((c) => c?.type === 'phone') || [];
    return phones[0]?.value || null;
  };

  const getFallbackEmail = () => {
    const fromContact = companyInfo?.contactInfo?.find((c) => c?.type === 'email');
    return fromContact?.value || null;
  };

  const buildWhatsappHref = (rawNumber, message) => {
    const number = (rawNumber || '').replace(/\D/g, '');
    if (!number) return null;
    const base = `https://wa.me/${number}`;
    if (message && message.trim()) {
      return `${base}?text=${encodeURIComponent(message.trim())}`;
    }
    return base;
  };

  const cm = header.contactMenu;
  const whatsappFloatingEnabled = companyInfo?.chatbot?.whatsappFlotante === true
    || companyInfo?.chatbot?.whatsappflotante === true;

  const iconForType = (type) => {
    switch (type) {
      case 'formulario': return FaRegFileLines;
      case 'whatsapp': return FaWhatsapp;
      case 'phone': return FaPhone;
      case 'email': return FaEnvelope;
      default: return null;
    }
  };

  // Resolve final href per item with fallbacks from companyInfo
  const resolveItemHref = (item) => {
    if (!item?.type) return null;
    switch (item.type) {
      case 'formulario': {
        if (item.scrollToSection) return `#${item.scrollToSection}`;
        return item.value || null;
      }
      case 'whatsapp': {
        const num = item.value || getFallbackWhatsappNumber();
        return buildWhatsappHref(num, item.whatsappMessage);
      }
      case 'phone': {
        const num = item.value || getFallbackPhone();
        return num ? `tel:${String(num).replace(/\s+/g, '')}` : null;
      }
      case 'email': {
        const mail = item.value || getFallbackEmail();
        return mail ? `mailto:${mail}` : null;
      }
      default: return null;
    }
  };

  const contactMenuLabel = typeof cm?.label === 'string' ? cm.label.trim() : '';
  const contactItems = (cm?.items || [])
    .filter((item) => !(whatsappFloatingEnabled && item?.type === 'whatsapp'))
    .map((item) => {
      const label = typeof item?.label === 'string' ? item.label.trim() : '';
      return {
        item,
        href: resolveItemHref(item),
        label,
        accessibleLabel: label || getContactTypeLabel(locale, item?.type),
      };
    })
    .filter(({ href, label }) => Boolean(href) && Boolean(label));
  const showContactMenu = contactItems.length > 0 && (contactItems.length === 1 || Boolean(contactMenuLabel));

  const renderContactMenu = (wrapperExtraClass = '') => {
    if (!showContactMenu) return null;

    const renderItem = (entry, className, { showIcon = true } = {}) => {
      const { item, href, label, accessibleLabel } = entry;
      const Icon = iconForType(item.type);
      const iconNode = showIcon && Icon ? (
        <Icon className={styles['header__submenu-icon']} aria-hidden="true" />
      ) : null;
      const onClickHandler = (e) => {
        if (item.type === 'formulario' && href.startsWith('#') && item.scrollToSection) {
          e.preventDefault();
          const el = document.getElementById(item.scrollToSection);
          if (el) el.scrollIntoView({ behavior: 'smooth' });
        }
        closeSubmenus();
        setMobileMenuOpen(false);
      };
      const isInternal = item.type === 'formulario' && href.startsWith('/');
      const isExternal = item.type === 'whatsapp';
      if (isInternal) {
        return (
          <Link to={href} className={className} onClick={onClickHandler} title={label} aria-label={accessibleLabel}>
            {iconNode}
            <span>{label}</span>
          </Link>
        );
      }
      return (
        <a
          href={href}
          className={className}
          onClick={onClickHandler}
          target={isExternal ? '_blank' : undefined}
          rel={isExternal ? 'noopener noreferrer' : undefined}
          title={label}
          aria-label={accessibleLabel}
        >
          {iconNode}
          <span>{label}</span>
        </a>
      );
    };

    if (contactItems.length === 1) {
      return (
        <div className={`${styles['header__contact-wrap']} ${wrapperExtraClass}`.trim()}>
          {renderItem(
            contactItems[0],
            `${styles['header__nav-item']} ${styles['header__nav-link']}`,
            { showIcon: false }
          )}
        </div>
      );
    }

    return (
      <div
        className={`${styles['header__contact-wrap']} ${wrapperExtraClass}`.trim()}
        onMouseEnter={handleContactMouseEnter}
        onMouseLeave={handleContactMouseLeave}
      >
        <div className={`${styles['header__nav-item']} ${styles['header__contact']} ${contactMenuOpen ? styles['header__contact--open'] : ''}`}>
          <button
            type="button"
            className={`${styles['header__nav-link']} ${styles['header__contact-trigger']} ${contactMenuOpen ? styles['header__contact-trigger--open'] : ''}`}
            aria-haspopup="true"
            aria-expanded={contactMenuOpen}
            aria-label={contactMenuLabel || getUiText(locale, 'contact')}
            onClick={handleContactTriggerClick}
          >
            <span>{contactMenuLabel}</span>
            <FaChevronDown className={styles['header__contact-chevron']} aria-hidden="true" />
          </button>
          <div className={styles.header__submenu}>
            {contactItems.map((entry, i) => (
              <React.Fragment key={i}>
                {renderItem(entry, styles['header__submenu-link'])}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Only show the switch when both a Spanish variant and English are actually available in Strapi
  const showLocaleSwitch =
    Array.isArray(availableLocales) &&
    availableLocales.some((l) => isSpanishLocale(l)) &&
    availableLocales.includes(ENGLISH_LOCALE);

  const renderLocaleSwitch = (extraClass = '') => {
    if (!showLocaleSwitch) return null;
    return (
    <button
      type="button"
      className={`${styles['header__locale']} ${extraClass}`.trim()}
      role="switch"
      aria-checked={isEnDisplay}
      aria-label={isEnDisplay ? 'Cambiar idioma a Español' : 'Switch language to English'}
      onClick={toggleLocale}
      data-active={isEnDisplay ? 'en' : 'es'}
    >
      <span className={styles['header__locale-track']}>
        <span className={styles['header__locale-thumb']} aria-hidden="true" />
        <span
          className={`${styles['header__locale-option']} ${isEnDisplay ? styles['header__locale-option--active'] : ''}`}
        >
          English
        </span>
        <span
          className={`${styles['header__locale-option']} ${!isEnDisplay ? styles['header__locale-option--active'] : ''}`}
        >
          Español
        </span>
      </span>
    </button>
    );
  };

  return (
    <header className={headerClassName} style={resolvedHeaderStyle}>
      {/* Overlay click to close mobile menu */}
      {mobileMenuOpen && (
        <div
          className={styles['header__overlay']}
          onClick={closeMobileMenu}
        />
      )}
      <div className={styles.header__container}>
        <div className={styles['header__section--left']}>
          <div className={styles['header__logo-wrap']}>
            <Link to={logoTo} className={styles.header__logo} title={getUiText(locale, 'goHome')}>
              {logoMedia.src ? (
                <img src={logoMedia.src} alt={logoMedia.alt} title={logoMedia.title} width={logoMedia.width} height={logoMedia.height} />
              ) : companyInfo?.companyName ? <span>{companyInfo.companyName}</span> : null}
            </Link>
          </div>
        </div>

        <div className={styles['header__section--right']}>
          <nav className={`${styles.header__nav} ${mobileMenuOpen ? styles['header__nav--open'] : ''}`}>
          {/* Mobile nav header: logo + close */}
          <div className={styles['header__nav-header']}>
            <Link
              to={logoTo}
              className={styles['header__nav-logo']}
              onClick={closeMobileMenu}
              title={getUiText(locale, 'goHome')}
            >
              {logoMedia.src ? (
                <img src={logoMedia.src} alt={logoMedia.alt} title={logoMedia.title} width={logoMedia.width} height={logoMedia.height} />
              ) : companyInfo?.companyName ? <span>{companyInfo.companyName}</span> : null}
            </Link>
            <button
              className={styles['header__nav-close']}
              aria-label={getUiText(locale, 'closeMenu')}
              onClick={closeMobileMenu}
            >
              <span></span>
              <span></span>
            </button>
          </div>

          {header.navigation?.map((item, index) => {
            const hasChildren = item.children?.length > 0;
            return (
            <div
              key={index}
              className={styles['header__nav-item']}
              onMouseEnter={() => handleNavItemMouseEnter(hasChildren)}
              onMouseLeave={() => handleNavItemMouseLeave(hasChildren)}
            >
              <a
                href={resolveUrlForLocale(item.url, locale) || `#${item.scrollToSection}`}
                className={styles['header__nav-link']}
                onClick={(e) => {
                  handleNavClick(e, item);
                  setMobileMenuOpen(false);
                }}
                target={item.openInNewTab ? '_blank' : undefined}
                rel={item.isExternal ? 'noopener noreferrer' : undefined}
                title={item.label}
              >
                {item.label}
              </a>

              {hasChildren && (
                <div className={styles.header__submenu}>
                  {item.children.map((child, childIndex) => {
                    const childPrice = getSlugPrice(child.url);
                    return (
                    <a
                      key={childIndex}
                      href={resolveUrlForLocale(child.url, locale) || `#${child.scrollToSection}`}
                      className={styles['header__submenu-link']}
                      onClick={(e) => {
                        handleNavClick(e, child);
                        setMobileMenuOpen(false);
                      }}
                      target={child.openInNewTab ? '_blank' : undefined}
                      rel={child.isExternal ? 'noopener noreferrer' : undefined}
                      title={child.label}
                    >
                      {child.label}
                      {childPrice && (
                        <span className={styles['header__submenu-price']}>{childPrice}</span>
                      )}
                    </a>
                    );
                  })}
                </div>
              )}
            </div>
            );
          })}

          {/* Contact menu inside mobile drawer (hidden on desktop via CSS) */}
          {renderContactMenu(styles['header__contact-wrap--mobile'])}

          {/* CTA inside mobile nav */}
          {header.ctaButton && (
            <a
              href={resolveUrlForLocale(header.ctaButton.url, locale) || `#${header.ctaButton.scrollToSection}`}
              className={`${styles.header__cta} ${styles['header__cta--mobile']} ${styles[`header__cta--${header.ctaButton.variant || 'primary'}`]}`}
              onClick={(e) => {
                handleNavClick(e, header.ctaButton);
                setMobileMenuOpen(false);
              }}
              title={header.ctaButton.label}
            >
              {header.ctaButton.label}
            </a>
          )}

          {/* Locale switch - pushed to the bottom of the drawer on mobile */}
          {renderLocaleSwitch(styles['header__locale--mobile'])}
        </nav>

        {/* Locale switch - desktop only, sibling of nav */}
        <div className={styles['header__locale-wrap']}>
          {renderLocaleSwitch(styles['header__locale--desktop'])}
        </div>

        {/* Contact menu - desktop only, sibling of nav */}
        {renderContactMenu(styles['header__contact-wrap--desktop'])}

        {header.ctaButton && (
          <a
            href={resolveUrlForLocale(header.ctaButton.url, locale) || `#${header.ctaButton.scrollToSection}`}
            className={`${styles.header__cta} ${styles['header__cta--desktop']} ${styles[`header__cta--${header.ctaButton.variant || 'primary'}`]}`}
            onClick={(e) => handleNavClick(e, header.ctaButton)}
            title={header.ctaButton.label}
          >
            {header.ctaButton.label}
          </a>
        )}

        <button
          className={`${styles['header__mobile-toggle']} ${mobileMenuOpen ? styles['header__mobile-toggle--open'] : ''}`}
          aria-label={mobileMenuOpen ? getUiText(locale, 'closeMenu') : getUiText(locale, 'openMenu')}
          onClick={() => {
            if (mobileMenuOpen) {
              closeMobileMenu();
              return;
            }

            setMobileMenuOpen(true);
          }}
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
        </div>{/* /header__section--right */}
      </div>
    </header>
  );
};

export default Header;
